import { Injectable } from '@angular/core';
import { BrowserName, BrowserEngine } from '@shared/enums/browser-info.enum';
import { BrowserInfo } from '@shared/models/browser-info.model';

@Injectable({ providedIn: 'root' })
export class BrowserInfoService {
  getBrowserInfo(): BrowserInfo {
    const ua = navigator.userAgent;

    return (
      this.detectBrave() ??
      this.detectDuckDuckGo(ua) ??
      this.detectSamsungInternet(ua) ??
      this.detectEdge(ua) ??
      this.detectOpera(ua) ??
      this.detectChrome(ua) ??
      this.detectFirefox(ua) ??
      this.detectSafari(ua) ??
      this.buildUnknown()
    );
  }

  // --------------------
  // Browser detectors
  // --------------------

  private detectBrave(): BrowserInfo | null {
    const nav = navigator as any;

    if (nav.brave && typeof nav.brave.isBrave === 'function') {
      return this.buildBrowser(
        BrowserName.Brave,
        undefined,
        BrowserEngine.Blink,
      );
    }

    return null;
  }

  private detectSamsungInternet(ua: string): BrowserInfo | null {
    if (!ua.includes('SamsungBrowser')) return null;

    return this.buildBrowser(
      BrowserName.SamsungInternet,
      this.extractVersion(ua, /SamsungBrowser\/([\d.]+)/),
      BrowserEngine.Blink,
    );
  }

  private detectDuckDuckGo(ua: string): BrowserInfo | null {
    if (!ua.includes('DuckDuckGo')) return null;

    return this.buildBrowser(
      BrowserName.DuckDuckGo,
      this.extractVersion(ua, /DuckDuckGo\/([\d.]+)/),
      BrowserEngine.Blink,
    );
  }

  private detectEdge(ua: string): BrowserInfo | null {
    if (!ua.includes('Edg/')) return null;

    return this.buildBrowser(
      BrowserName.Edge,
      this.extractVersion(ua, /Edg\/([\d.]+)/),
      BrowserEngine.Blink,
    );
  }

  private detectChrome(ua: string): BrowserInfo | null {
    if (!ua.includes('Chrome/') || ua.includes('Edg/') || ua.includes('OPR/')) {
      return null;
    }

    return this.buildBrowser(
      BrowserName.Chrome,
      this.extractVersion(ua, /Chrome\/([\d.]+)/),
      BrowserEngine.Blink,
    );
  }

  private detectFirefox(ua: string): BrowserInfo | null {
    if (!ua.includes('Firefox/')) return null;

    return this.buildBrowser(
      BrowserName.Firefox,
      this.extractVersion(ua, /Firefox\/([\d.]+)/),
      BrowserEngine.Gecko,
    );
  }

  private detectSafari(ua: string): BrowserInfo | null {
    if (!ua.includes('Safari') || ua.includes('Chrome')) return null;

    return this.buildBrowser(
      BrowserName.Safari,
      this.extractVersion(ua, /Version\/([\d.]+)/),
      BrowserEngine.WebKit,
    );
  }

  private detectOpera(ua: string): BrowserInfo | null {
    if (!ua.includes('OPR/')) return null;

    return this.buildBrowser(
      BrowserName.Opera,
      this.extractVersion(ua, /OPR\/([\d.]+)/),
      BrowserEngine.Blink,
    );
  }

  // --------------------
  // Helpers
  // --------------------

  private buildBrowser(
    name: BrowserName,
    version: string | undefined,
    engine: BrowserEngine,
  ): BrowserInfo {
    const parts: string[] = [name];

    if (version) {
      parts.push(version);
    }

    return {
      name,
      version,
      engine,
      label: parts.join(' '),
    };
  }

  private buildUnknown(): BrowserInfo {
    return {
      name: BrowserName.Unknown,
      engine: BrowserEngine.Unknown,
      label: 'Unknown Browser',
    };
  }

  private extractVersion(ua: string, regex: RegExp): string | undefined {
    const match = ua.match(regex);
    return match ? match[1] : undefined;
  }
}
